/**
 * 认证管理 Composable
 */

import { ref, computed } from "vue";
import { httpClient } from "@/api/client";

// Token 存储键名
const TOKEN_KEY = "kotori-api-token";

// 认证状态
const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));

/**
 * 使用认证
 */
export function useAuth() {
  /**
   * 是否已认证
   */
  const isAuthenticated = computed(() => !!token.value);

  /**
   * 获取当前 token
   */
  const getToken = () => token.value;

  /**
   * 检查认证状态
   */
  const checkAuth = (): boolean => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      token.value = storedToken;
      return true;
    }
    return false;
  };

  /**
   * 登录
   * @param apiToken API Token
   * @returns 是否验证成功
   */
  const login = async (apiToken: string): Promise<boolean> => {
    try {
      // 保存 token
      localStorage.setItem(TOKEN_KEY, apiToken);
      token.value = apiToken;

      // 验证 token 有效性（发送测试请求）
      try {
        await httpClient.get("/tasks?limit=1");
        return true;
      } catch (error: any) {
        // 如果收到 401，token 无效
        if (error.code === 401) {
          // 清除 token
          localStorage.removeItem(TOKEN_KEY);
          token.value = null;
          throw new Error("API Token 无效");
        }
        // 其他错误可能是网络问题，但 token 可能有效
        // 仍然允许登录
        return true;
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * 登出
   */
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    token.value = null;
  };

  /**
   * 初始化认证（从 localStorage 恢复）
   */
  const initAuth = () => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      token.value = storedToken;
    }
  };

  return {
    token,
    isAuthenticated,
    getToken,
    checkAuth,
    login,
    logout,
    initAuth,
  };
}

// 导出常量
export { TOKEN_KEY };
